import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useSite } from "../context/SiteContext";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/motion";
import SEO from "../components/SEO";
import { IMAGES, PRODUCTS } from "../data/products";

const Hero = () => {
  const { t } = useLanguage();
  const { img } = useSite();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        setMouse({ x: (e.clientX - r.width / 2) / r.width, y: (e.clientY - r.height / 2) / r.height });
      }}
      className="relative flex min-h-screen items-center overflow-hidden pt-24"
    >
      <div className="pointer-events-none absolute -right-32 top-24 hidden font-display text-[26rem] font-bold leading-none text-ink/[0.04] select-none lg:block" aria-hidden="true">
        OI
      </div>
      <div className="mx-auto grid w-full max-w-[1400px] gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-12">
        <motion.div style={{ y: textY }} className="relative z-10 flex flex-col justify-center lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="overline-label text-rouge"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </motion.p>
          <h1 className="mt-8 font-display text-[17vw] font-bold leading-[0.9] tracking-tighter sm:text-[13vw] lg:text-[7.5rem] xl:text-[8.5rem]" data-testid="hero-heading">
            <MaskedLines
              delay={0.35}
              lines={[
                <span className="font-medium italic text-stone2">{t.hero.line1}</span>,
                <span>{t.hero.line2}</span>,
                <span>
                  On <span className="italic text-rouge">It!</span>
                </span>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 max-w-md text-base leading-relaxed text-stone2 md:text-lg"
            data-testid="hero-subtext"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.25 }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/behandelingen#boeken"
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-3 bg-rouge px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge-dark"
            >
              {t.hero.cta1}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/collectie"
              data-testid="hero-collection-button"
              className="link-underline text-xs font-bold uppercase tracking-[0.2em] text-ink"
            >
              {t.hero.cta2}
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-14 text-xs uppercase tracking-[0.3em] text-stone2/70"
          >
            {t.hero.badge}
          </motion.p>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ y: imgY }}
            className="img-frame relative aspect-[3/4] max-h-[72vh] w-full"
          >
            <motion.img
              src={img("heroMain", IMAGES.heroMain)}
              alt="Handgemaakte press-on nagels Golden Desert Bloom van On It Nails Herzele"
              className="h-full w-full object-cover"
              animate={{ x: mouse.x * -14, y: mouse.y * -14 }}
              transition={{ type: "spring", stiffness: 40, damping: 20 }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: -3 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="absolute -bottom-10 -left-6 w-40 border-4 border-cream shadow-2xl lg:-left-16 lg:w-52 animate-float-slow"
          >
            <img src={img("heroSecondary", IMAGES.heroSecondary)} alt="Nail art detail — On It Nails atelier" className="aspect-square w-full object-cover" loading="lazy" />
          </motion.div>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 1.4 }}
            className="absolute -right-4 top-8 origin-left bg-ink px-5 py-3 text-cream lg:-right-8"
          >
            <p className="font-display text-lg italic">est. Herzele</p>
          </motion.div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 lg:left-12"
      >
        <span className="h-px w-12 bg-ink/30" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-stone2">{t.hero.scroll}</span>
      </motion.div>
    </section>
  );
};

const EditorialMarquee = () => {
  const { t } = useLanguage();
  return (
    <section className="border-y border-ink/10 bg-sand py-7" data-testid="editorial-marquee" aria-hidden="true">
      <Marquee speed={28} gradient={false} pauseOnHover>
        {t.marquee.map((item, i) => (
          <span key={i} className="mx-12 flex items-center gap-12 font-display text-2xl italic text-ink/70 lg:text-3xl">
            {item}
            <Star size={14} className="text-rouge" fill="currentColor" />
          </span>
        ))}
      </Marquee>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLanguage();
  const routes = ["/hoe-het-werkt", "/behandelingen", "/collectie"];
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-40" data-testid="manifesto-section">
      <FadeUp>
        <p className="overline-label text-rouge">{t.manifesto.overline}</p>
        <h2 className="mt-6 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          {t.manifesto.title} <span className="italic font-medium text-rouge">{t.manifesto.titleItalic}</span>
        </h2>
      </FadeUp>
      <div className="mt-20 grid gap-px bg-ink/10 lg:grid-cols-3">
        {t.manifesto.chapters.map((ch, i) => (
          <FadeUp key={ch.nr} delay={i * 0.12} className="group relative flex flex-col bg-cream p-10 transition-colors duration-500 hover:bg-sand lg:p-12">
            <span className="font-display text-6xl font-bold text-blush transition-colors duration-500 group-hover:text-rouge" aria-hidden="true">
              {ch.nr}
            </span>
            <h3 className="mt-8 font-display text-2xl font-bold lg:text-3xl">{ch.title}</h3>
            <p className="mt-5 flex-1 text-sm leading-relaxed text-stone2">{ch.text}</p>
            <Link
              to={routes[i]}
              data-testid={`manifesto-link-${ch.nr}`}
              className="mt-10 inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:text-rouge"
            >
              {ch.link}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const CollectionTeaser = () => {
  const { t } = useLanguage();
  const featured = PRODUCTS.slice(0, 3);
  return (
    <section className="bg-sand py-28 lg:py-40" data-testid="collection-teaser">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <FadeUp>
            <p className="overline-label text-rouge">{t.collectionTeaser.overline}</p>
            <h2 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              {t.collectionTeaser.title}
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-stone2">{t.collectionTeaser.sub}</p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/collectie"
              data-testid="teaser-view-all"
              className="group inline-flex items-center gap-3 border border-ink px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream"
            >
              {t.collectionTeaser.cta}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
        <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.12} className={i === 1 ? "lg:translate-y-12" : ""}>
              <Link to="/collectie" className="group block" data-testid={`teaser-product-${p.id}`}>
                <div className="img-frame aspect-[4/5]">
                  <img
                    src={p.img}
                    alt={`Press on nagels ${p.name} — On It Nails Herzele`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-5 flex items-baseline justify-between">
                  <h3 className="font-display text-xl font-bold">{p.name}</h3>
                  <p className="text-sm font-semibold text-rouge">
                    {p.from && <span className="mr-1 text-xs font-medium text-stone2">{t.collectionTeaser.from}</span>}
                    €{p.price},00
                  </p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t } = useLanguage();
  const [idx, setIdx] = useState(0);
  const items = t.testimonials.items;

  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(id);
  }, [items.length]);

  return (
    <section className="bg-ink py-28 text-cream lg:py-40" data-testid="testimonials-section">
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="overline-label text-blush">{t.testimonials.overline}</p>
            <h2 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
              {t.testimonials.title} <span className="italic font-medium text-blush">{t.testimonials.titleItalic}</span>
            </h2>
            <div className="mt-12 flex gap-3">
              <button
                onClick={() => setIdx((idx - 1 + items.length) % items.length)}
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
                className="border border-cream/25 p-3 transition-colors duration-300 hover:border-blush hover:text-blush"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setIdx((idx + 1) % items.length)}
                aria-label="Next testimonial"
                data-testid="testimonial-next"
                className="border border-cream/25 p-3 transition-colors duration-300 hover:border-blush hover:text-blush"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-12 hidden lg:block">
            <div className="img-frame aspect-[4/5] max-w-xs">
              <img src={IMAGES.testimonials[idx % IMAGES.testimonials.length]} alt={`Klantresultaat ${items[idx].name} — nagels On It Nails`} loading="lazy" className="h-full w-full object-cover" />
            </div>
          </FadeUp>
        </div>
        <div className="flex flex-col justify-center lg:col-span-8 lg:pl-16">
          <span className="font-display text-[8rem] leading-none text-rouge" aria-hidden="true">"</span>
          <div className="relative min-h-[220px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid="testimonial-quote"
              >
                <p className="font-display text-2xl font-medium italic leading-snug sm:text-3xl lg:text-4xl">
                  {items[idx].quote}
                </p>
                <footer className="mt-8 flex items-center gap-4">
                  <span className="h-px w-10 bg-blush" />
                  <span className="text-sm font-bold uppercase tracking-[0.25em] text-blush" data-testid="testimonial-name">
                    {items[idx].name}
                  </span>
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>
          <div className="mt-10 flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1 transition-all duration-500 ${i === idx ? "w-10 bg-blush" : "w-4 bg-cream/25 hover:bg-cream/50"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const GiftCard = () => {
  const { t } = useLanguage();
  const { img } = useSite();
  return (
    <section className="mx-auto max-w-[1400px] px-6 py-28 lg:px-12 lg:py-40" data-testid="giftcard-section">
      <div className="grid items-center gap-16 lg:grid-cols-2">
        <FadeUp className="relative">
          <ParallaxImage
            src={img("giftcard", IMAGES.giftcard)}
            alt="Gift Card On It Nails — Only for you"
            className="aspect-[4/3]"
            speed={0.08}
          />
          <div className="absolute -bottom-6 -right-4 bg-rouge px-6 py-4 text-cream lg:-right-8">
            <p className="font-display text-2xl italic">Only for you</p>
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="overline-label text-rouge">{t.giftcard.overline}</p>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
            {t.giftcard.title} <span className="italic font-medium text-rouge">{t.giftcard.titleItalic}</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-stone2">{t.giftcard.text}</p>
          <a
            href="https://www.onitnails.com/onitnailscollectie/p/gift-card"
            target="_blank"
            rel="noreferrer"
            data-testid="giftcard-cta"
            className="group mt-10 inline-flex items-center gap-3 bg-ink px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge"
          >
            {t.giftcard.cta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </FadeUp>
      </div>
    </section>
  );
};

const Home = () => (
  <>
    <SEO page="home" />
    <Hero />
    <EditorialMarquee />
    <Manifesto />
    <CollectionTeaser />
    <Testimonials />
    <GiftCard />
  </>
);

export default Home;
